/**
 * Guide track (docs/DUBBING.md, «Дорожка оригинальных реплик — не мусор»).
 *
 * Пока актёр пишет свою реплику, в наушниках звучит оригинальная — с дорожки
 * `dialogue`. Попадать в тайминг и интонацию так заметно проще.
 *
 * Отдельный AudioContext и отдельный граф: у превью-микшера свой контекст,
 * врезанный в <video> через MediaElementSource, и во время записи он выключен.
 * В рендер guide-дорожка не идёт никогда — она живёт только в браузере.
 */

import { ClipScheduler } from './clips'
import type { Clip } from './clips'
import { loadTrackBuffer, peekTrackBuffer } from './trackBuffers'

export type GuideTrack = { id: string; url: string }

export type GuideStatus = 'absent' | 'idle' | 'loading' | 'ready' | 'error'

export type GuideState = {
  status: GuideStatus
  error: string | null
  /** Звучит ли дорожка прямо сейчас (включена на время записи). */
  active: boolean
}

export class GuideTrackPlayer {
  private readonly scheduler: ClipScheduler
  private ctx: AudioContext | null = null
  private track: GuideTrack | null = null
  private buffer: AudioBuffer | null = null
  private status: GuideStatus = 'absent'
  private error: string | null = null
  private loadingId: string | null = null
  private gainDb = -6
  private active = false
  private disposed = false
  private onStateChange: ((state: GuideState) => void) | null = null

  constructor(video: HTMLVideoElement) {
    this.scheduler = new ClipScheduler({
      video,
      getContext: () => this.ctx,
      getClips: () => this.collectClips(),
      isActive: () => this.active && !this.disposed,
    })
  }

  setStateListener(listener: ((state: GuideState) => void) | null): void {
    this.onStateChange = listener
    this.emitState()
  }

  getStatus(): GuideStatus {
    return this.status
  }

  isReady(): boolean {
    return this.buffer !== null
  }

  setTrack(track: GuideTrack | null): void {
    if (this.disposed || this.track?.id === track?.id) return
    this.track = track
    this.buffer = null
    this.loadingId = null
    this.error = null
    this.status = track ? 'idle' : 'absent'
    this.scheduler.stopAll()
    this.emitState()
  }

  setGainDb(db: number): void {
    this.gainDb = db
    if (this.active) this.scheduler.reschedule()
  }

  /**
   * Скачать и декодировать дорожку заранее, не начиная воспроизведение.
   * Вызывается при включении тумблера — к моменту записи буфер уже готов.
   */
  async preload(): Promise<void> {
    if (this.disposed) return
    const track = this.track
    if (!track || this.buffer || this.loadingId === track.id) return
    const ctx = this.ensureContext()
    const cached = peekTrackBuffer(ctx, track.id)
    if (cached) {
      this.buffer = cached
      this.status = 'ready'
      this.emitState()
      return
    }
    this.loadingId = track.id
    this.status = 'loading'
    this.error = null
    this.emitState()
    try {
      const buffer = await loadTrackBuffer(ctx, track.id, track.url)
      if (this.disposed || this.track?.id !== track.id) return
      this.buffer = buffer
      this.status = 'ready'
    } catch (err) {
      if (this.disposed || this.track?.id !== track.id) return
      this.status = 'error'
      this.error = err instanceof Error ? err.message : String(err)
    } finally {
      if (this.loadingId === track.id) this.loadingId = null
      this.emitState()
      if (this.active) this.scheduler.reschedule()
    }
  }

  /** Включается на время записи и выключается сразу после неё. */
  async setActive(active: boolean): Promise<void> {
    if (this.disposed || this.active === active) return
    this.active = active
    if (active) {
      const ctx = this.ensureContext()
      if (ctx.state === 'suspended') await ctx.resume().catch(() => undefined)
      // Декодирование не ждём: запись не должна замирать на длинной дорожке,
      // а готовый буфер подхватится ближайшим reschedule() из preload().
      void this.preload()
      this.scheduler.reschedule()
    } else {
      this.scheduler.stopAll()
    }
    this.emitState()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.scheduler.dispose()
    this.buffer = null
    this.onStateChange = null
    const ctx = this.ctx
    this.ctx = null
    if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined)
  }

  private ensureContext(): AudioContext {
    if (this.ctx) return this.ctx
    const ctx = new AudioContext()
    this.ctx = ctx
    return ctx
  }

  private collectClips(): Clip[] {
    if (!this.buffer) return []
    return [{ buffer: this.buffer, startAt: 0, gainDb: this.gainDb }]
  }

  private emitState(): void {
    this.onStateChange?.({ status: this.status, error: this.error, active: this.active })
  }
}
